import type { SchedulerReport } from '../domain/services';
import type { Clock, Unsubscribe } from './types';

export interface Scheduler {
  /** Runs once now, then every tick and on every clock change. */
  start(): void;
  stop(): void;
  /** One pass of the job; overlapping calls are coalesced into one follow-up pass. */
  run(): Promise<SchedulerReport | undefined>;
  last(): SchedulerReport | undefined;
  /** Called after each completed pass. */
  subscribe(cb: (r: SchedulerReport) => void): Unsubscribe;
}

export function createScheduler(opts: {
  job: () => Promise<SchedulerReport>;
  clock: Clock;
  tickMs: number;
  timers?: { set: (fn: () => void, ms: number) => unknown; clear: (h: unknown) => void };
}): Scheduler {
  const timers = opts.timers ?? { set: (fn, ms) => setInterval(fn, ms), clear: (h) => clearInterval(h as ReturnType<typeof setInterval>) };
  const listeners = new Set<(r: SchedulerReport) => void>();
  let running: Promise<SchedulerReport | undefined> | undefined;
  let again = false;
  let handle: unknown;
  let unsubClock: Unsubscribe | undefined;
  let lastReport: SchedulerReport | undefined;

  const pass = async (): Promise<SchedulerReport | undefined> => {
    try {
      lastReport = await opts.job();
      for (const cb of [...listeners]) cb(lastReport);
      return lastReport;
    } finally {
      running = undefined;
      if (again) { again = false; void run(); }
    }
  };
  function run(): Promise<SchedulerReport | undefined> {
    if (running) { again = true; return running; }
    running = pass();
    return running;
  }

  return {
    start() {
      if (handle !== undefined) return;
      handle = timers.set(() => void run().catch(() => undefined), opts.tickMs);
      unsubClock = opts.clock.subscribe(() => void run().catch(() => undefined));
      void run().catch(() => undefined);
    },
    stop() {
      if (handle !== undefined) timers.clear(handle);
      handle = undefined;
      unsubClock?.();
      unsubClock = undefined;
    },
    run,
    last: () => lastReport,
    subscribe(cb) { listeners.add(cb); return () => void listeners.delete(cb); },
  };
}
